import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Play, RefreshCw, Bug, Calendar, Clock, Database, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { directPublishPost, manualPublishPost } from '../lib/facebookPublisher';

interface Task {
  id: string;
  content: string;
  platform: string;
  scheduled_for: string;
  webhook_url?: string;
  cycle: string;
  status: string;
}

interface DebugLog {
  id?: string;
  event_type?: string;
  details?: any;
  check_timestamp?: string;
  task_timezone?: string;
  task_scheduled_for?: string;
  task_local_time?: string;
  current_utc?: string;
  current_local?: string;
  should_run?: boolean;
  details_text?: string;
  created_at?: string;
  timestamp?: string;
  type?: string;
}

export function TaskDebugger() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isCreatingDirect, setIsCreatingDirect] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [taskStatus, setTaskStatus] = useState<any>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isCheckingCron, setIsCheckingCron] = useState(false);
  const [cronStatus, setCronStatus] = useState<any>(null);
  const [isRunningCron, setIsRunningCron] = useState(false);
  const [readyPosts, setReadyPosts] = useState<any[]>([]);
  const [isLoadingReadyPosts, setIsLoadingReadyPosts] = useState(false);
  const [isFixingStuckPosts, setIsFixingStuckPosts] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    if (selectedTask) {
      loadLogs();
      loadTaskStatus();
      loadReadyPosts();
    }
  }, [selectedTask]);

  const loadTasks = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found');
        return;
      }

      const { data, error } = await supabase
        .from('scheduled_tasks')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setIsLoading(false);
    }
  };

  const loadReadyPosts = async () => {
    if (!selectedTask) return;

    try {
      setIsLoadingReadyPosts(true);
      
      // Get ready posts from ready_posts table
      const { data: readyPostsData, error: readyPostsError } = await supabase
        .from('ready_posts')
        .select('*')
        .eq('task_id', selectedTask.id)
        .order('created_at', { ascending: false });

      if (readyPostsError) throw readyPostsError;
      
      setReadyPosts(readyPostsData || []);
    } catch (error) {
      console.error('Error loading ready posts:', error);
      toast.error('Failed to load ready posts');
    } finally {
      setIsLoadingReadyPosts(false);
    }
  };

  const checkCronStatus = async () => {
    try {
      setIsCheckingCron(true);
      
      // Call the check_cron_job_status function
      const { data, error } = await supabase.rpc('check_cron_job_status');
      
      if (error) throw error;
      
      setCronStatus(data);
      toast.success('Cron job status checked successfully');
    } catch (error) {
      console.error('Error checking cron job status:', error);
      toast.error(`Error checking cron job status: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Set a default status when there's an error
      setCronStatus({
        success: false,
        error: error instanceof Error ? error.message : 'Failed to check cron status',
        all_jobs: [{
          jobname: 'auto_publish_ready_posts',
          schedule: '* * * * *',
          command: 'Publish ready posts',
          active: true
        }]
      });
    } finally {
      setIsCheckingCron(false);
    }
  };

  const runCronManually = async () => {
    try {
      setIsRunningCron(true);
      
      // Call the trigger_auto_publish_ready_posts function
      const { data, error } = await supabase.rpc('trigger_auto_publish_ready_posts');
      
      if (error) throw error;
      
      toast.success('Auto publish ready posts triggered manually');
      
      // Refresh data
      await loadLogs();
      await loadTaskStatus();
      await loadReadyPosts();
    } catch (error) {
      console.error('Error running cron manually:', error);
      toast.error(`Error running cron manually: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRunningCron(false);
    }
  };

  const debugReadyPosts = async () => {
    try {
      setIsLoadingReadyPosts(true);
      
      // Call the debug_ready_posts function
      const { data, error } = await supabase.rpc('debug_ready_posts');
      
      if (error) throw error;
      
      console.log('Ready posts debug info:', data);
      toast.success('Ready posts debug info logged to console');
    } catch (error) {
      console.error('Error debugging ready posts:', error);
      toast.error(`Error debugging ready posts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingReadyPosts(false);
    }
  };

  const fixStuckPosts = async () => {
    try {
      setIsFixingStuckPosts(true);
      
      // Call the fix_stuck_publishing_posts function
      const { data, error } = await supabase.rpc('fix_stuck_publishing_posts');
      
      if (error) throw error;
      
      console.log('Fixed stuck posts:', data);
      toast.success(`Fixed ${data.count_fixed} stuck posts`);
      
      // Refresh data
      await loadLogs();
      await loadTaskStatus();
      await loadReadyPosts();
    } catch (error) {
      console.error('Error fixing stuck posts:', error);
      toast.error(`Error fixing stuck posts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsFixingStuckPosts(false);
    }
  };

  const executeTaskNow = async () => {
    if (!selectedTask) return;

    setIsExecuting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use the force_publish_post function to directly publish a post
      const { data, error } = await supabase.rpc('force_publish_post', {
        p_task_id: selectedTask.id,
        p_user_id: user.id
      });

      if (error) throw error;
      
      if (!data || !data.success) {
        throw new Error(data?.error || 'Unknown error occurred during publishing');
      }
      
      toast.success('Post published successfully');
      await loadLogs();
      await loadTaskStatus();
      await loadReadyPosts();
    } catch (error) {
      console.error('Error executing task:', error);
      toast.error(`Error executing task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const loadLogs = async () => {
    if (!selectedTask) return;

    setIsRefreshing(true);
    try {
      // Get timezone debug logs
      const { data: timezoneLogs, error: timezoneError } = await supabase
        .from('timezone_debug_log')
        .select('*')
        .eq('task_id', selectedTask.id)
        .order('check_timestamp', { ascending: false })
        .limit(20);

      if (timezoneError) throw timezoneError;

      // Get task logs
      const { data: taskLogs, error: taskLogsError } = await supabase
        .from('scheduled_tasks_log')
        .select('*')
        .eq('details->>task_id', selectedTask.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (taskLogsError) throw taskLogsError;

      // Combine and format logs
      const formattedTimezoneLogs = (timezoneLogs || []).map(log => ({
        ...log,
        type: 'timezone',
        timestamp: log.check_timestamp,
        details_text: log.details
      }));

      const formattedTaskLogs = (taskLogs || []).map(log => ({
        ...log,
        type: 'task',
        timestamp: log.created_at,
        details_text: JSON.stringify(log.details, null, 2)
      }));

      // Combine, sort by timestamp (newest first), and limit to 50
      const combinedLogs = [...formattedTimezoneLogs, ...formattedTaskLogs]
        .sort((a, b) => {
          const dateA = new Date(a.timestamp || a.created_at || a.check_timestamp || 0);
          const dateB = new Date(b.timestamp || b.created_at || b.check_timestamp || 0);
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 50);

      setLogs(combinedLogs);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast.error('Failed to load logs');
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadTaskStatus = async () => {
    if (!selectedTask) return;

    try {
      setIsLoadingStatus(true);
      
      // Use the debug_task RPC function to get comprehensive task status
      const { data, error } = await supabase.rpc('debug_task', {
        p_task_id: selectedTask.id
      });

      if (error) throw error;
      
      setTaskStatus(data);
    } catch (error) {
      console.error('Error loading task status:', error);
      toast.error(`Error loading task status: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const createTestPost = async () => {
    if (!selectedTask) return;

    try {
      setIsTesting(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use the force_publish_post function to directly publish a post
      const { data, error } = await supabase.rpc('force_publish_post', {
        p_task_id: selectedTask.id,
        p_user_id: user.id
      });

      if (error) throw error;
      
      if (!data || !data.success) {
        throw new Error(data?.error || 'Unknown error occurred during publishing');
      }
      
      toast.success('Post published successfully');
      await loadLogs();
      await loadTaskStatus();
      await loadReadyPosts();
    } catch (error) {
      console.error('Error creating test post:', error);
      toast.error(`Error creating test post: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsTesting(false);
    }
  };

  const createDirectPost = async () => {
    if (!selectedTask) return;

    try {
      setIsCreatingDirect(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use the force_publish_post function to directly publish a post
      const { data, error } = await supabase.rpc('force_publish_post', {
        p_task_id: selectedTask.id,
        p_user_id: user.id
      });

      if (error) throw error;
      
      if (!data || !data.success) {
        throw new Error(data?.error || 'Unknown error occurred during publishing');
      }
      
      toast.success('Post published successfully');
      await loadLogs();
      await loadTaskStatus();
      await loadReadyPosts();
    } catch (error) {
      const errorMessage = error instanceof Error ? 
        error.message : 
        typeof error === 'object' ? 
          (error as any)?.message || JSON.stringify(error) : 
          'Unknown error occurred';
          
      console.error('Error creating direct post:', error);
      toast.error(`Error creating direct post: ${errorMessage}`);
    } finally {
      setIsCreatingDirect(false);
    }
  };

  const renderLogItem = (log: DebugLog) => {
    if (log.type === 'timezone') {
      return (
        <div key={log.id} className="p-3 bg-blue-50 rounded-lg mb-2">
          <div className="flex justify-between items-start">
            <h4 className="text-sm font-medium">Timezone Check</h4>
            <span className="text-xs text-gray-500">
              {log.check_timestamp ? format(new Date(log.check_timestamp), 'dd.MM.yyyy, HH:mm:ss') : ''}
            </span>
          </div>
          <p className="text-sm mt-1">Should run: {log.should_run ? 'Yes' : 'No'}</p>
          <p className="text-sm mt-1">Details: {log.details}</p>
        </div>
      );
    } else {
      return (
        <div key={log.id} className="p-3 bg-purple-50 rounded-lg mb-2">
          <div className="flex justify-between items-start">
            <h4 className="text-sm font-medium">Task Log</h4>
            <span className="text-xs text-gray-500">
              {log.created_at ? format(new Date(log.created_at), 'dd.MM.yyyy, HH:mm:ss') : ''}
            </span>
          </div>
          <p className="text-sm mt-1">Event: {log.event_type}</p>
          <p className="text-sm mt-1 whitespace-pre-wrap font-mono text-xs">
            Details: {typeof log.details === 'object' ? JSON.stringify(log.details, null, 2) : log.details_text}
          </p>
        </div>
      );
    }
  };

  const renderTaskStatus = () => {
    if (isLoadingStatus) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      );
    }

    if (!taskStatus) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
          No status information available
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-2">Task Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">Status:</span> {taskStatus.task?.status}</div>
            <div><span className="font-medium">Platform:</span> {taskStatus.task?.platform}</div>
            <div><span className="font-medium">Cycle:</span> {taskStatus.task?.cycle}</div>
            <div><span className="font-medium">Timezone:</span> {taskStatus.task?.timezone}</div>
            <div><span className="font-medium">Scheduled For:</span> {taskStatus.task?.scheduled_for ? format(new Date(taskStatus.task.scheduled_for), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
          </div>
        </div>

        <div className="p-4 bg-green-50 rounded-lg">
          <h4 className="font-medium mb-2">Timing Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">Current Time (UTC):</span> {taskStatus.timing?.current_time ? format(new Date(taskStatus.timing.current_time), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
            <div><span className="font-medium">Current Time (Local):</span> {taskStatus.timing?.local_current_time ? format(new Date(taskStatus.timing.local_current_time), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
            <div><span className="font-medium">Scheduled Time (Local):</span> {taskStatus.timing?.local_scheduled_time ? format(new Date(taskStatus.timing.local_scheduled_time), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
            <div><span className="font-medium">Minute Difference:</span> {taskStatus.timing?.minute_diff}</div>
            <div><span className="font-medium">Should Run:</span> {taskStatus.timing?.should_run ? 'Yes' : 'No'}</div>
          </div>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg">
          <h4 className="font-medium mb-2">Status Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">Has Active Posts:</span> {taskStatus.status?.has_active_posts ? 'Yes' : 'No'}</div>
            <div><span className="font-medium">Has Locks:</span> {taskStatus.status?.has_locks ? 'Yes' : 'No'}</div>
            <div><span className="font-medium">Last Execution:</span> {taskStatus.status?.last_execution ? format(new Date(taskStatus.status.last_execution), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
            <div><span className="font-medium">Last Execution Status:</span> {taskStatus.status?.last_execution_status || 'N/A'}</div>
          </div>
        </div>

        {taskStatus.posts && taskStatus.posts.length > 0 && (
          <div className="p-4 bg-yellow-50 rounded-lg">
            <h4 className="font-medium mb-2">Recent Posts</h4>
            <div className="space-y-2">
              {taskStatus.posts.map((post: any, index: number) => (
                <div key={index} className="p-2 bg-white rounded border border-yellow-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                    <div><span className="font-medium">ID:</span> {post.id}</div>
                    <div><span className="font-medium">Status:</span> {post.status}</div>
                    <div><span className="font-medium">Stage:</span> {post.stage}</div>
                    <div><span className="font-medium">In Progress:</span> {post.is_in_progress ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">Created:</span> {post.created_at ? format(new Date(post.created_at), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderReadyPosts = () => {
    if (isLoadingReadyPosts) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      );
    }

    if (!readyPosts || readyPosts.length === 0) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
          No ready posts available
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {readyPosts.map((post) => (
          <div key={post.id} className={`p-3 rounded-lg border ${post.is_default ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-medium">{post.is_default ? 'Default Ready Post' : 'Ready Post'}</h4>
              <span className="text-xs text-gray-500">
                {format(new Date(post.created_at), 'dd.MM.yyyy, HH:mm:ss')}
              </span>
            </div>
            <p className="text-sm mt-1 line-clamp-2">{post.content}</p>
            <div className="mt-2 flex gap-2">
              {!post.is_default && (
                <button
                  onClick={async () => {
                    try {
                      await supabase
                        .from('ready_posts')
                        .update({ is_default: true })
                        .eq('id', post.id);
                      
                      toast.success('Post set as default');
                      await loadReadyPosts();
                    } catch (error) {
                      console.error('Error setting post as default:', error);
                      toast.error('Failed to set post as default');
                    }
                  }}
                  className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
                >
                  Set as Default
                </button>
              )}
              <button
                onClick={() => {
                  console.log('Ready post details:', post);
                  toast.success('Ready post details logged to console');
                }}
                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCronStatus = () => {
    if (isCheckingCron) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      );
    }

    if (!cronStatus) {
      return (
        <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
          No cron job status available
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-2">Cron Job Status</h4>
          {cronStatus.success ? (
            <div>
              {cronStatus.cron_job && cronStatus.cron_job.length > 0 ? (
                cronStatus.cron_job.map((job: any, index: number) => (
                  <div key={index} className="bg-white p-3 rounded-lg border border-blue-200 mb-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div><span className="font-medium">Job Name:</span> {job.jobname}</div>
                      <div><span className="font-medium">Schedule:</span> {job.schedule}</div>
                      <div><span className="font-medium">Command:</span> {job.command}</div>
                      <div><span className="font-medium">Active:</span> {job.active ? 'Yes' : 'No'}</div>
                      <div><span className="font-medium">Last Run:</span> {job.last_run ? format(new Date(job.last_run), 'dd.MM.yyyy, HH:mm:ss') : 'Never'}</div>
                      <div><span className="font-medium">Next Run:</span> {job.next_run ? format(new Date(job.next_run), 'dd.MM.yyyy, HH:mm:ss') : 'N/A'}</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-red-600">No auto-publish cron job found</p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-red-600">{cronStatus.error}</p>
              {cronStatus.all_jobs && (
                <div className="mt-2">
                  <h5 className="font-medium">All Jobs:</h5>
                  <div className="mt-1 space-y-1">
                    {cronStatus.all_jobs.map((job: any, index: number) => (
                      <div key={index} className="text-sm">
                        {job.jobname}: {job.schedule} - {job.command} ({job.active ? 'Active' : 'Inactive'})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Bug className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-bold">Task Debugger</h2>
        <button 
          onClick={loadTasks} 
          className="ml-auto p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold mb-2">Cron Job Status</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={checkCronStatus}
              disabled={isCheckingCron}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Database className="w-5 h-5" />
              <span>Check Cron Status</span>
              {isCheckingCron && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
            
            <button
              onClick={runCronManually}
              disabled={isRunningCron}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Play className="w-5 h-5" />
              <span>Run Cron Manually</span>
              {isRunningCron && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
            
            <button
              onClick={debugReadyPosts}
              disabled={isLoadingReadyPosts}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Bug className="w-5 h-5" />
              <span>Debug Ready Posts</span>
              {isLoadingReadyPosts && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
            
            <button
              onClick={fixStuckPosts}
              disabled={isFixingStuckPosts}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <AlertCircle className="w-5 h-5" />
              <span>Fix Stuck Posts</span>
              {isFixingStuckPosts && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
          </div>
          
          {renderCronStatus()}
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-2">Select a Task</h3>
          <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto">
            {tasks.map(task => (
              <div 
                key={task.id}
                className={`p-4 border rounded-lg cursor-pointer ${selectedTask?.id === task.id ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'}`}
                onClick={() => setSelectedTask(task)}
              >
                <div className="font-medium">{task.content}</div>
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {format(parseISO(task.scheduled_for), 'dd.MM.yyyy, HH:mm:ss')}
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {task.timezone}
                  </div>
                </div>
                {selectedTask?.id === task.id && (
                  <div className="absolute right-2 top-2 text-green-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedTask && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={executeTaskNow}
              disabled={isExecuting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Play className="w-5 h-5" />
              <span>Execute Task Now</span>
              {isExecuting && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
            
            <button
              onClick={createTestPost}
              disabled={isTesting}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Create Test Post</span>
              {isTesting && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
            
            <button
              onClick={createDirectPost}
              disabled={isCreatingDirect}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              <span>Create Direct Post</span>
              {isCreatingDirect && <RefreshCw className="w-4 h-4 animate-spin" />}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bug className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Task Status</h3>
                  <button 
                    onClick={loadTaskStatus} 
                    className="ml-auto p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                  >
                    <RefreshCw className={`w-5 h-5 ${isLoadingStatus ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {renderTaskStatus()}
              </div>
            </div>
            
            <div>
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bug className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Ready Posts</h3>
                  <button 
                    onClick={loadReadyPosts} 
                    className="ml-auto p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                  >
                    <RefreshCw className={`w-5 h-5 ${isLoadingReadyPosts ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                {renderReadyPosts()}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <Bug className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Debug Logs</h3>
              <button 
                onClick={loadLogs} 
                className="ml-auto p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto p-2">
              {logs.length > 0 ? (
                logs.map(log => renderLogItem(log))
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                  No logs found for this task
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}